import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { connect } from 'react-redux'
import { Button, CircularProgress, Snackbar } from '@material-ui/core'
import axios from 'axios'
import ServiceSidebar from './ServiceSidebar'
import ServiceDetail from './ServiceDetail'
import RightRail from './RightRail'
import ScenarioTabs from '../../estimating/ScenarioTabs'
import MarginSlider from '../../estimating/MarginSlider'
import { tokens } from '../../../styles/desertFloraTheme'

const EstimatingCockpit = (props) => {
  const { projectId } = useParams()
  const history = useHistory()
  const { url } = props

  const [project, setProject] = useState(null)
  const [projectServices, setProjectServices] = useState([])
  const [services, setServices] = useState([])
  const [selectedServiceId, setSelectedServiceId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [recalcMsg, setRecalcMsg] = useState('')
  const [lastUpdated, setLastUpdated] = useState(null)
  const [mutationInFlight, setMutationInFlight] = useState(false)
  const [marginTarget, setMarginTarget] = useState(25)
  const [scenarios, setScenarios] = useState([])
  const [activeScenarioId, setActiveScenarioId] = useState(null)

  const fetchData = useCallback(async (silent) => {
    try {
      if (!silent) setLoading(true)
      const [projRes, psRes, svcRes] = await Promise.all([
        axios.get(`${url}/api/bid-estimation/projects/${projectId}`),
        axios.get(`${url}/api/bid-estimation/projects/${projectId}/project-services`),
        axios.get(`${url}/api/bid-estimation/services`)
      ])
      setProject(projRes.data)
      setProjectServices(Array.isArray(psRes.data) ? psRes.data : [])
      setServices(Array.isArray(svcRes.data) ? svcRes.data : [])
      setLastUpdated(Date.now())
    } catch (err) {
      console.error('EstimatingCockpit fetch error:', err)
    } finally {
      setLoading(false)
      setMutationInFlight(false)
    }
  }, [url, projectId])

  useEffect(() => { fetchData(false) }, [fetchData])

  const refreshSilent = useCallback(() => {
    setMutationInFlight(true)
    return fetchData(true)
  }, [fetchData])

  const handleServiceAdded = () => refreshSilent()
  const handleServiceRemoved = () => {
    setSelectedServiceId(null)
    refreshSilent()
  }
  const handleServiceUpdated = () => refreshSilent()

  const selectedService = projectServices.find(ps => ps._id === selectedServiceId)

  const projectTotals = useMemo(() => {
    const cost = (projectServices || []).reduce((s, ps) => s + (ps.totalCost || 0), 0)
    const sell = (projectServices || []).reduce((s, ps) => s + (ps.totalSell || 0), 0)
    const margin = sell > 0 ? ((sell - cost) / sell * 100) : 0
    return { cost, sell, margin }
  }, [projectServices])

  const handleSnapshot = (name) => {
    const id = '__snap_' + Date.now()
    setScenarios(prev => [...prev, {
      id,
      name,
      total: projectTotals.sell,
      snapshot: JSON.parse(JSON.stringify(projectServices))
    }])
    setActiveScenarioId(id)
  }

  const recalcAge = lastUpdated ? Math.round((Date.now() - lastUpdated) / 1000) : null

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: tokens.bg }}>
        <CircularProgress style={{ color: tokens.primary }} />
      </div>
    )
  }

  const isComputing = mutationInFlight

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', flexDirection: 'column', background: tokens.bg, fontFamily: tokens.fontUi }}>
      {/* ── Topbar ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 16,
        padding: '12px 24px', minHeight: 56,
        background: tokens.surface, borderBottom: `1px solid ${tokens.line}`,
        flexShrink: 0
      }}>
        {/* Breadcrumbs + H1 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div style={{ fontSize: 12, color: tokens.ink3, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{ cursor: 'pointer' }}
              onClick={() => history.push('/bid-estimation')}
            >Projects</span>
            <span style={{ color: tokens.ink4 }}>/</span>
            <span
              style={{ cursor: 'pointer' }}
              onClick={() => history.push(`/bid-estimation/${projectId}`)}
            >{project?.name || 'Project'}</span>
            <span style={{ color: tokens.ink4 }}>/</span>
            <span>Estimating</span>
          </div>
          <h1 style={{
            fontFamily: "'Instrument Serif', Georgia, 'Times New Roman', serif",
            fontSize: 24, fontWeight: 400, color: tokens.ink, margin: 0, lineHeight: 1.2
          }}>
            Estimating <span style={{ color: tokens.ink3 }}>— {project?.name || ''}</span>
          </h1>
        </div>

        {/* Recalc status indicator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 16 }}>
          <span style={{
            width: 7, height: 7, borderRadius: '50%',
            background: isComputing ? tokens.accent : tokens.ok,
            animation: isComputing ? 'ec-pulse 1s ease-in-out infinite' : 'none',
            flexShrink: 0
          }} />
          <span style={{ fontSize: 11, color: tokens.ink3, fontFamily: tokens.fontMono }}>
            {isComputing
              ? 'Auto-calculating...'
              : recalcAge != null
                ? `Synced ${recalcAge}s ago`
                : 'Ready'}
          </span>
        </div>

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Scenario tabs */}
        <ScenarioTabs
          scenarios={scenarios}
          activeId={activeScenarioId}
          onChange={setActiveScenarioId}
          onSnapshot={handleSnapshot}
          projectTotals={projectTotals}
        />

        {/* Margin slider */}
        <MarginSlider
          target={marginTarget}
          actual={projectTotals.margin}
          totalCost={projectTotals.cost}
          totalSell={projectTotals.sell}
          onChange={setMarginTarget}
        />

        {/* Actions */}
        <Button variant="outlined" size="small" onClick={() => console.log('TODO: Export')}>
          Export
        </Button>
        <Button variant="contained" color="primary" size="small" onClick={() => console.log('TODO: Send for review')}>
          Send for review
        </Button>
      </div>

      {/* ── 3-column body ── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '300px 1fr 320px',
        flex: 1, overflow: 'hidden'
      }}>
        {/* Left sidebar */}
        <ServiceSidebar
          projectServices={projectServices}
          services={services}
          selectedServiceId={selectedServiceId}
          onSelectService={setSelectedServiceId}
          onServiceAdded={handleServiceAdded}
          onServiceRemoved={handleServiceRemoved}
          projectId={projectId}
          url={url}
        />

        {/* Center workspace */}
        <div style={{
          overflow: 'auto', padding: '24px 32px',
          background: tokens.bg,
          display: 'flex', flexDirection: 'column', alignItems: 'center'
        }}>
          <div style={{ width: '100%', maxWidth: 1100 }}>
            <Snackbar
              open={!!recalcMsg}
              autoHideDuration={3000}
              onClose={() => setRecalcMsg('')}
              message={recalcMsg}
            />

            {selectedService ? (
              <ServiceDetail
                projectService={selectedService}
                project={project}
                url={url}
                onUpdate={handleServiceUpdated}
              />
            ) : (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', minHeight: 400, gap: 12
              }}>
                <div style={{
                  fontSize: 14, color: tokens.ink3, textAlign: 'center', maxWidth: 320
                }}>
                  Pick a service from the left, or <strong style={{ color: tokens.primary }}>+ Add</strong> to start.
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right rail */}
        <RightRail
          projectService={selectedService}
          projectServices={projectServices}
          project={project}
        />
      </div>

      {/* Pulse animation for recalc indicator */}
      <style>{`
        @keyframes ec-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.4); }
        }
      `}</style>
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user
})

export default connect(mapStateToProps)(EstimatingCockpit)
